import { useCallback, useEffect, useRef } from 'react'
import { ColumnState, GridApi } from 'ag-grid-community'
import { AgGridStateDto, useGetAgGridStateQuery, useSaveAgGridStateMutation } from '@/features/ag-grid-state/ag-grid-state.api.ts'
import { getLocalStorageItem, setLocalStorageItem } from '@/utils/local-storage.ts'

/*
 * Grid layouts come from three sources, and each has exactly one entry point:
 *
 *   first load               → localStorage (falls back to backend, then code default)
 *   "Restore Saved Layout"   → backend
 *   "Restore Default Layout" → code default (column defs + `applyDefaultLayout`)
 *
 * localStorage is the working copy. Every user change to columns or filters is written there, so a
 * refresh returns to what the user was last looking at. The backend copy only changes on Save Layout.
 * Both restore actions write their result to localStorage too, so a refresh keeps the restored layout.
 */

const AG_GRID_STATE_CACHE_PREFIX = 'ag-grid-state:'

/** Prefix of columns AG Grid creates itself: the auto group column and the selection column. */
const GRID_GENERATED_COL_ID_PREFIX = 'ag-Grid-'

function getCacheKey(gridId: string) {
  return `${AG_GRID_STATE_CACHE_PREFIX}${gridId}`
}

const isGridGeneratedColId = (colId: string) => colId.startsWith(GRID_GENERATED_COL_ID_PREFIX)

const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value)

const hasFilters = (model: Record<string, unknown> | null | undefined): model is Record<string, unknown> =>
  !!model && typeof model === 'object' && Object.keys(model).length > 0

/**
 * Validate a single persisted column state entry before it is handed to AG Grid.
 *
 * The persisted payload comes from localStorage or the backend and is typed but
 * never verified, so a serialization defect anywhere in that round trip lands
 * straight in AG Grid's column model. AG Grid does not reject bad values, it
 * acts on them: a truthy non-boolean `hide` hides the column, a non-string
 * `aggFunc` silently drops the column from the value columns (and logs error
 * #33), and a non-numeric `width`/`flex` leaves the column at whatever width it
 * happened to have before the restore. The result is a grid with missing or
 * mis-sized columns.
 *
 * Wrong-typed values are therefore dropped rather than passed through. An
 * absent key makes AG Grid skip that attribute, so the column keeps its
 * columnDefs default. `null` is kept where AG Grid treats it as an explicit
 * "clear this attribute", because that is what getColumnState() emits.
 */
function sanitizeColumnStateItem(raw: unknown): ColumnState | null {
  if (!raw || typeof raw !== 'object') return null
  const item = raw as Record<string, unknown>
  if (typeof item.colId !== 'string' || item.colId.length === 0) return null

  const clean: ColumnState = { colId: item.colId }

  if (typeof item.hide === 'boolean') clean.hide = item.hide
  if (typeof item.rowGroup === 'boolean') clean.rowGroup = item.rowGroup
  if (typeof item.pivot === 'boolean') clean.pivot = item.pivot
  if (isFiniteNumber(item.width) && item.width > 0) clean.width = item.width
  if (isFiniteNumber(item.flex) || item.flex === null) clean.flex = item.flex as number | null
  if (isFiniteNumber(item.sortIndex) || item.sortIndex === null) clean.sortIndex = item.sortIndex as number | null
  if (isFiniteNumber(item.rowGroupIndex) || item.rowGroupIndex === null) clean.rowGroupIndex = item.rowGroupIndex as number | null
  if (isFiniteNumber(item.pivotIndex) || item.pivotIndex === null) clean.pivotIndex = item.pivotIndex as number | null
  if (typeof item.aggFunc === 'string' || item.aggFunc === null) clean.aggFunc = item.aggFunc as string | null
  if (item.sort === 'asc' || item.sort === 'desc' || item.sort === null) clean.sort = item.sort
  if (item.pinned === 'left' || item.pinned === 'right' || typeof item.pinned === 'boolean' || item.pinned === null) {
    clean.pinned = item.pinned
  }

  return clean
}

/** Validate a persisted column state array, dropping malformed and duplicate entries. */
function sanitizeColumnState(raw: unknown): ColumnState[] {
  if (!Array.isArray(raw)) return []
  const seenColIds = new Set<string>()
  const sanitized: ColumnState[] = []
  for (const item of raw) {
    const clean = sanitizeColumnStateItem(item)
    if (!clean?.colId || seenColIds.has(clean.colId)) continue
    seenColIds.add(clean.colId)
    sanitized.push(clean)
  }
  return sanitized
}

/** A layout worth applying: at least one valid column entry or one filter.
 *  A record with neither (e.g. an empty backend row) counts as "nothing saved". */
function isUsableLayout(layout: unknown): layout is AgGridStateDto {
  if (!layout || typeof layout !== 'object') return false
  const dto = layout as AgGridStateDto
  return sanitizeColumnState(dto.columnState).length > 0 || hasFilters(dto.filterModel)
}

function readLocalLayout(gridId: string | undefined): AgGridStateDto | null {
  if (!gridId) return null
  const cached = getLocalStorageItem(getCacheKey(gridId), null)
  return isUsableLayout(cached) ? cached : null
}

/** True when the error is the backend saying "this user has no saved layout for this grid". */
function isNotFoundError(error: unknown): boolean {
  return !!error && typeof error === 'object' && 'status' in error && (error as { status: unknown }).status === 404
}

/**
 * Apply a persisted column layout.
 *
 * Grid-generated columns (auto group, selection) are kept even though the auto group column does
 * not exist yet when the grid starts ungrouped: AG Grid creates it from the `rowGroup` entries in the
 * same applyColumnState() call and then applies the auto column's own entry (width, pin, position).
 * Dropping it, as an earlier version did, made the saved order un-restorable for every grouped layout.
 *
 * Entries for data columns this grid does not have are dropped. The same gridId can be reused by
 * grids whose column set varies at runtime, and AG Grid discards the whole ordering when the state
 * references unknown columns in a way that splits a column group (warning #39).
 *
 * Width and flex are passed through as saved. getColumnState() emits `flex: null` for every column
 * the user resized (AG Grid clears flex on a manual resize), and AG Grid only reads `width` when
 * `flex` is null, so resized columns keep their pixel width while untouched columns keep flexing.
 */
function applyColumnLayout(api: GridApi, gridId: string, rawState: unknown) {
  const sanitized = sanitizeColumnState(rawState)
  if (sanitized.length === 0) return

  const currentColIds = new Set(api.getColumnState().map(col => col.colId))
  const applicable = sanitized.filter(col => isGridGeneratedColId(col.colId) || currentColIds.has(col.colId))
  if (applicable.length === 0) return

  // An entry with a width but no flex key (older or hand-edited data) would otherwise keep the
  // column's default flex, which makes AG Grid ignore the width.
  const state = applicable.map(col => (col.width != null && col.flex === undefined ? { ...col, flex: null } : col))

  // Only restore the order when the layout describes exactly the data columns this grid has.
  // A partial order is ambiguous and can break married column groups. Grid-generated columns
  // are left out of the comparison because their presence depends on grouping / selection.
  const savedDataColCount = sanitized.filter(col => !isGridGeneratedColId(col.colId)).length
  const applicableDataColCount = applicable.filter(col => !isGridGeneratedColId(col.colId)).length
  const currentDataColCount = [...currentColIds].filter(colId => !isGridGeneratedColId(colId)).length
  const applyOrder = applicableDataColCount === savedDataColCount && applicableDataColCount === currentDataColCount

  if (import.meta.env.DEV && !applyOrder) {
    console.warn(
      `[useAgGridStatePersistence] Persisted layout for "${gridId}" does not match the current columns; column order not restored.`,
      { persisted: savedDataColCount, applicable: applicableDataColCount, gridColumns: currentDataColCount },
    )
  }

  api.applyColumnState({ state, applyOrder })

  // Recalculate header layout after the container has settled.
  requestAnimationFrame(() => {
    if (!api.isDestroyed()) api.refreshHeader()
  })
}

/** Apply a filter model. Must run after row data is loaded, because set filters build their value
 *  list from the rows. */
function applyFilterLayout(api: GridApi, filterModel: Record<string, unknown> | null) {
  if (!hasFilters(filterModel)) {
    api.setFilterModel(null)
    return
  }
  try {
    api.setFilterModel(filterModel)
  } catch {
    // Fallback: apply filters per column so one bad entry does not drop the rest
    for (const [colId, model] of Object.entries(filterModel)) {
      try {
        api.setColumnFilterModel(colId, model)
      } catch { /* ignore individual column errors */ }
    }
    api.onFilterChanged()
  }
}

interface UseAgGridStatePersistenceOptions {
  /**
   * Applies the parts of the code default that column defs cannot express, such as default row
   * grouping. Runs after resetColumnState() on "Restore Default Layout", and on first load when
   * neither localStorage nor the backend has a layout.
   */
  applyDefaultLayout?: (api: GridApi) => void
}

/** "Nothing pending" marker for the filter model, distinct from `null` ("clear all filters"). */
const NO_PENDING_FILTER = Symbol('no-pending-filter')
type PendingFilter = Record<string, unknown> | null | typeof NO_PENDING_FILTER

/**
 * Persist and restore AG Grid layout (column state + filters). See the header comment for which
 * source is used when.
 *
 * Wire-up: call `onGridReady` / `onFirstDataRendered` from the matching grid events and
 * `persistState` from every column / filter change event.
 */
export function useAgGridStatePersistence(gridId: string | undefined, options?: UseAgGridStatePersistenceOptions) {
  const {
    data: backendLayout,
    isSuccess: backendLoaded,
    isError: backendFailed,
    refetch: refetchBackendLayout,
  } = useGetAgGridStateQuery(gridId!, { skip: !gridId })
  const [saveToBackend] = useSaveAgGridStateMutation()
  const backendSettled = !gridId || backendLoaded || backendFailed

  const applyDefaultLayoutRef = useRef(options?.applyDefaultLayout)
  useEffect(() => { applyDefaultLayoutRef.current = options?.applyDefaultLayout }, [options?.applyDefaultLayout])

  const gridApiRef = useRef<GridApi | null>(null)
  const firstDataRenderedRef = useRef(false)
  /** True once a layout has been chosen for this grid instance (initial load or an explicit restore). */
  const layoutResolvedRef = useRef(false)
  /** Filter model waiting for the first data render. */
  const pendingFilterRef = useRef<PendingFilter>(NO_PENDING_FILTER)
  /** Column / filter events are ignored until the initial layout is fully in place. */
  const persistenceEnabledRef = useRef(false)
  /** Bumped on every programmatic layout change; persistence is muted until the matching release. */
  const suppressTokenRef = useRef(0)
  const suppressedRef = useRef(false)

  /** Mute persistState while a programmatic layout change settles. AG Grid Enterprise's column
   *  tool panel emits follow-up column events a frame or two later, hence the double rAF. */
  const suppressPersistence = useCallback(() => {
    const token = ++suppressTokenRef.current
    suppressedRef.current = true
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (suppressTokenRef.current === token) suppressedRef.current = false
      })
    })
  }, [])

  const captureLayout = useCallback((): AgGridStateDto | null => {
    const api = gridApiRef.current
    if (!gridId || !api || api.isDestroyed()) return null
    const pending = pendingFilterRef.current
    const filterModel = pending !== NO_PENDING_FILTER ? pending : api.getFilterModel()
    return {
      gridId,
      columnState: api.getColumnState(),
      filterModel: hasFilters(filterModel) ? filterModel : null,
      sortModel: null,
    }
  }, [gridId])

  const writeLocalLayout = useCallback(() => {
    const layout = captureLayout()
    if (layout && gridId) setLocalStorageItem(getCacheKey(gridId), layout)
  }, [captureLayout, gridId])

  /** Filters wait for the first data render; once data is there they apply immediately. */
  const applyOrQueueFilters = useCallback((api: GridApi, filterModel: Record<string, unknown> | null) => {
    if (firstDataRenderedRef.current) {
      pendingFilterRef.current = NO_PENDING_FILTER
      applyFilterLayout(api, filterModel)
    } else {
      pendingFilterRef.current = filterModel
    }
  }, [])

  /** Apply a stored layout (localStorage or backend). */
  const applyStoredLayout = useCallback((api: GridApi, layout: AgGridStateDto) => {
    if (!gridId) return
    suppressPersistence()
    applyColumnLayout(api, gridId, layout.columnState)
    applyOrQueueFilters(api, hasFilters(layout.filterModel) ? layout.filterModel : null)
  }, [gridId, suppressPersistence, applyOrQueueFilters])

  /** Apply the code default: column defs (via resetColumnState) plus `applyDefaultLayout`.
   *
   *  `resetExisting` is false on first load: the columns were just created from the defs and no
   *  filter has been set, so only `applyDefaultLayout` has anything to do. */
  const applyCodeDefaultLayout = useCallback((api: GridApi, { resetExisting }: { resetExisting: boolean }) => {
    suppressPersistence()
    if (resetExisting) {
      // resetColumnState() reads the `initial*` attributes from the column defs.
      api.resetColumnState()
      applyOrQueueFilters(api, null)
    }
    applyDefaultLayoutRef.current?.(api)
  }, [suppressPersistence, applyOrQueueFilters])

  /** Turn on persistence once the initial layout (columns and filters) is fully applied. */
  const enablePersistenceIfReady = useCallback(() => {
    if (persistenceEnabledRef.current) return
    if (layoutResolvedRef.current && firstDataRenderedRef.current) {
      persistenceEnabledRef.current = true
    }
  }, [])

  /**
   * First-load resolution: localStorage → backend → code default.
   *
   * localStorage is read synchronously at grid ready, so a returning user never sees a flash of
   * the default layout. Without a local copy the choice waits for the backend request to settle;
   * the grid shows its column-def defaults meanwhile.
   */
  const resolveInitialLayout = useCallback(() => {
    const api = gridApiRef.current
    if (layoutResolvedRef.current || !api || api.isDestroyed()) return

    const localLayout = readLocalLayout(gridId)
    if (localLayout) {
      layoutResolvedRef.current = true
      applyStoredLayout(api, localLayout)
      enablePersistenceIfReady()
      return
    }

    if (!backendSettled) return // wait for the backend; the effect below calls back in
    layoutResolvedRef.current = true

    // Deferred to the next frame: this can run from onGridReady or from a React effect while AG
    // Grid's React header components are still committing. A column change (especially
    // setRowGroupColumns, which adds the auto group column and rebuilds every header controller)
    // at that moment can mount a header against an already-destroyed controller and throw
    // "Cannot read properties of null (reading 'getProvidedColumnGroup')".
    requestAnimationFrame(() => {
      // The grid may have been destroyed (navigation, unmount) before this fired.
      if (api.isDestroyed()) return
      if (isUsableLayout(backendLayout)) {
        applyStoredLayout(api, backendLayout)
      } else {
        applyCodeDefaultLayout(api, { resetExisting: false })
      }
      enablePersistenceIfReady()
    })
  }, [gridId, backendSettled, backendLayout, applyStoredLayout, applyCodeDefaultLayout, enablePersistenceIfReady])

  // Retry the first-load resolution once the backend request settles.
  useEffect(() => {
    resolveInitialLayout()
  }, [resolveInitialLayout])

  const onGridReady = useCallback((api: GridApi) => {
    gridApiRef.current = api
    resolveInitialLayout()
  }, [resolveInitialLayout])

  const onFirstDataRendered = useCallback((api: GridApi) => {
    firstDataRenderedRef.current = true
    const pending = pendingFilterRef.current
    if (pending !== NO_PENDING_FILTER) {
      pendingFilterRef.current = NO_PENDING_FILTER
      suppressPersistence()
      applyFilterLayout(api, pending)
    }
    enablePersistenceIfReady()
  }, [suppressPersistence, enablePersistenceIfReady])

  /** Write the current layout to localStorage. Call from column / filter change events. */
  const persistState = useCallback(() => {
    if (!persistenceEnabledRef.current || suppressedRef.current) return
    writeLocalLayout()
  }, [writeLocalLayout])

  /** "Save Layout": store the current layout on the backend (and locally). */
  const saveState = useCallback(async () => {
    const layout = captureLayout()
    if (!layout || !gridId) throw new Error('No grid state to save')
    setLocalStorageItem(getCacheKey(gridId), layout)
    const result = await saveToBackend(layout)
    if ('error' in result) throw result.error
  }, [captureLayout, gridId, saveToBackend])

  /**
   * "Restore Saved Layout": apply the layout stored on the backend.
   *
   * Refetches rather than reading the cached query result, so a layout saved from another browser
   * or machine since this page loaded is picked up.
   *
   * @returns false when there is no saved layout; throws when the backend request fails.
   */
  const restoreSavedLayout = useCallback(async (): Promise<boolean> => {
    if (!gridId) return false

    let layout: AgGridStateDto | undefined
    try {
      layout = await refetchBackendLayout().unwrap()
    } catch (error) {
      if (isNotFoundError(error)) return false
      throw error
    }

    const api = gridApiRef.current
    if (!api || api.isDestroyed()) return false
    if (!isUsableLayout(layout)) return false

    layoutResolvedRef.current = true
    applyStoredLayout(api, layout)
    writeLocalLayout()
    enablePersistenceIfReady()
    return true
  }, [gridId, refetchBackendLayout, applyStoredLayout, writeLocalLayout, enablePersistenceIfReady])

  /**
   * "Restore Default Layout": apply the code default.
   *
   * The result is written to localStorage so a refresh keeps showing the default. Removing the
   * local copy instead would make the next load fall back to the backend layout.
   */
  const restoreDefault = useCallback(() => {
    const api = gridApiRef.current
    if (!api || api.isDestroyed()) return
    layoutResolvedRef.current = true
    applyCodeDefaultLayout(api, { resetExisting: true })
    writeLocalLayout()
    enablePersistenceIfReady()
  }, [applyCodeDefaultLayout, writeLocalLayout, enablePersistenceIfReady])

  // No unmount cleanup is needed: every deferred callback checks api.isDestroyed(), and nulling the
  // API ref in an effect cleanup would break StrictMode's mount → unmount → mount cycle in dev,
  // because onGridReady does not fire a second time.

  return {
    onGridReady,
    onFirstDataRendered,
    persistState,
    saveState,
    restoreDefault,
    restoreSavedLayout,
  }
}
